if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        const activeMedia = this.elements.viewer.querySelector('.is-active');
        if (activeMedia) this.playActiveMedia(activeMedia);

        // Add observer for stacked layout or scrolling
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.playActiveMedia(entry.target, true);
            }
          });
        }, { threshold: 0.4 }); // Higher threshold to avoid multiple simultaneous triggers

        this.elements.viewer.querySelectorAll('.product__media-item').forEach(item => {
          this.observer.observe(item);
        });

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.dataset.desktopLayout === 'custom_grid') {
          this.initCustomGridClicks();
        }
      }

      get mql() {
        return window.matchMedia('(min-width: 990px)');
      }

      initCustomGridClicks() {
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.addEventListener(
            'click',
            (event) => {
              const hero = this.getCustomGridHero();
              if (!hero || item === hero) return;

              event.preventDefault();
              event.stopImmediatePropagation();
              this.activateCustomGridMedia(item.dataset.mediaId);
            },
            true
          );
        });
      }

      getCustomGridHero() {
        return this.elements.viewer.querySelector('.product__media-list .product__media-item:first-child');
      }

      activateCustomGridMedia(mediaId) {
        const hero = this.getCustomGridHero();
        const target = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!hero || !target || target === hero) return;

        if (this.mql.matches) {
          this.swapMediaItems(hero, target);
        } else {
          this.setActiveMedia(mediaId, false);
        }

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((el) => el.classList.remove('is-active'));
        const active = this.getCustomGridHero();
        active?.classList.add('is-active');
        if (active) this.playActiveMedia(active);
      }

      swapMediaItems(a, b) {
        const parent = a.parentNode;
        if (!parent || parent !== b.parentNode) return;

        const aNext = a.nextElementSibling;
        const bNext = b.nextElementSibling;

        if (aNext === b) {
          parent.insertBefore(b, a);
        } else if (bNext === a) {
          parent.insertBefore(a, b);
        } else {
          parent.insertBefore(a, bNext);
          parent.insertBefore(b, aNext);
        }

        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem, isAutoplay = false) {
        if (!isAutoplay) window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          const startVideo = () => {
            deferredMedia.loadContent(false);
            const video = deferredMedia.querySelector('video');
            if (video && video.paused) {
              video.muted = true;
              video.playsInline = true;
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(error => {
                  if (error.name !== 'AbortError') console.error("Autoplay failed:", error);
                });
              }
            }
          };

          if (deferredMedia.loadContent) {
            startVideo();
          } else {
            customElements.whenDefined('deferred-media').then(startVideo);
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
